<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <v-row class="mb-2" align="center" dense>
      <v-col cols="6" sm="3">
        <dropdown
          v-model="filterPeriod"
          :items="periodOptions"
          itemText="label"
          itemValue="value"
          :label="$t('finance_period_label')"
          hideDetails
        />
      </v-col>
      <v-col cols="6" sm="3">
        <dropdown
          v-model="filterStatus"
          :items="statusOptions"
          itemText="label"
          itemValue="value"
          :label="$t('finance_status_label')"
          clearable
          hideDetails
        />
      </v-col>
      <v-col cols="12" sm="6" class="text-right">
        <primary-button @click="openAddDialog">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t("finance_report_deposit") }}
        </primary-button>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="filteredDeposits"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        :no-data-text="$t('finance_no_deposits_yet')"
        mobile-breakpoint="0"
        @click:row="openDeposit"
      >
        <template #item.amount="{ item }">{{ formatCurrency(item.amount, restaurantCurrency) }}</template>
        <template #item.date="{ item }">{{ formatDate(item.date) }}</template>
        <template #item.reference="{ item }">
          <span class="mono">{{ item.reference }}</span>
        </template>
        <template #item.status="{ item }">
          <v-chip small :color="statusMeta(item.status).color" text-color="white">
            {{ $t(statusMeta(item.status).labelKey) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <deposit-detail-dialog
      v-model="detailOpen"
      :deposit="selectedDeposit"
      @edit="openEditDialog"
      @delete="confirmDelete"
    />

    <deposit-form-dialog
      v-model="dialogOpen"
      :saving="saving"
      :deposit="editingDeposit"
      @save="onSaveDeposit"
    />
  </div>
</template>

<script>
import gpsApi from "@/plugins/gpsApi";
import hasSnackbar from "@/mixins/hasSnackbar";
import GlobalConfirmBox from "@/components/global/GlobalConfirmBox.vue";
import DepositDetailDialog from "./dialogs/DepositDetailDialog.vue";
import DepositFormDialog from "./dialogs/DepositFormDialog.vue";
import { DEPOSIT_STATUS_META, formatCurrency, formatDate } from "./constants";

const mapDepositDto = (dto) => ({
  id: dto.id,
  date: dto.date,
  amount: dto.amount,
  method: dto.method,
  reference: dto.reference,
  status: dto.status,
  proof: dto.proof,
  bankTransaction: dto.bank_transaction,
  recordedBy: dto.recorded_by,
  note: dto.note,
});

const isWithinPeriod = (date, value) => {
  if (value === "all") return true;
  const target = new Date(date);
  const now = new Date();
  if (value === "year") return target.getFullYear() === now.getFullYear();
  const days = value === "30d" ? 30 : 90;
  return now.getTime() - target.getTime() <= days * 24 * 60 * 60 * 1000;
};

export default {
  name: "FinanceTabDeposits",

  components: { GlobalConfirmBox, DepositDetailDialog, DepositFormDialog },

  mixins: [hasSnackbar],

  data() {
    return {
      deposits: [],
      loading: false,
      saving: false,
      filterPeriod: "all",
      filterStatus: null,
      detailOpen: false,
      selectedDeposit: null,
      dialogOpen: false,
      editingDeposit: null,
    };
  },

  computed: {
    restaurantId() {
      return this.$store.state.restaurant.id;
    },
    periodOptions() {
      return [
        { label: this.$t("finance_all"), value: "all" },
        { label: this.$t("finance_last_30_days"), value: "30d" },
        { label: this.$t("finance_last_90_days"), value: "90d" },
        { label: this.$t("finance_this_year"), value: "year" },
      ];
    },
    headers() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
        { text: this.$t("finance_method"), value: "method" },
        { text: this.$t("finance_reference"), value: "reference" },
        { text: this.$t("finance_status_label"), value: "status" },
      ];
    },
    statusOptions() {
      return Object.keys(DEPOSIT_STATUS_META).map((value) => ({
        value,
        label: this.$t(DEPOSIT_STATUS_META[value].labelKey),
      }));
    },
    filteredDeposits() {
      return this.deposits.filter(
        (deposit) =>
          isWithinPeriod(deposit.date, this.filterPeriod) &&
          (!this.filterStatus || deposit.status === this.filterStatus)
      );
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    formatCurrency,
    formatDate,

    statusMeta(status) {
      return DEPOSIT_STATUS_META[status] || { labelKey: "", color: "grey" };
    },

    async refresh() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const { data } = await gpsApi.get(`/restaurants/${this.restaurantId}/deposits`);
        const list = (data && (data.data || data)) || [];
        this.deposits = list.map(mapDepositDto);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_deposits_load_error"));
      } finally {
        this.loading = false;
      }
    },

    openDeposit(deposit) {
      this.selectedDeposit = deposit;
      this.detailOpen = true;
    },

    openAddDialog() {
      this.editingDeposit = null;
      this.dialogOpen = true;
    },

    openEditDialog(deposit) {
      this.editingDeposit = deposit;
      this.detailOpen = false;
      this.dialogOpen = true;
    },

    async onSaveDeposit(payload) {
      this.saving = true;
      try {
        if (this.editingDeposit) {
          const { data } = await gpsApi.put(
            `/deposits/${this.editingDeposit.id}`,
            payload
          );
          const updated = mapDepositDto(data.data || data);
          this.deposits = this.deposits.map((d) => (d.id === updated.id ? updated : d));
          this.showSuccessSnackbar(this.$t("finance_deposit_saved"));
        } else {
          const { data } = await gpsApi.post(
            `/restaurants/${this.restaurantId}/deposits`,
            payload
          );
          const created = mapDepositDto(data.data || data);
          this.deposits = [created, ...this.deposits];
          this.showSuccessSnackbar(this.$t("finance_deposit_reported"));
        }
        this.dialogOpen = false;
      } catch (error) {
        this.showErrorSnackbar(
          this.editingDeposit
            ? this.$t("finance_deposit_save_error")
            : this.$t("finance_deposit_report_error")
        );
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(deposit) {
      this.detailOpen = false;
      this.$refs.confirmBox
        .open(this.$t("finance_delete_deposit_title"), this.$t("finance_confirm_delete_deposit", { amount: formatCurrency(deposit.amount, this.restaurantCurrency) }), {
          color: "error",
        })
        .then(() => this.removeDeposit(deposit))
        .catch(() => {});
    },

    async removeDeposit(deposit) {
      try {
        await gpsApi.delete(`/deposits/${deposit.id}`);
        this.deposits = this.deposits.filter((d) => d.id !== deposit.id);
        this.showSuccessSnackbar(this.$t("finance_deposit_deleted"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_deposit_delete_error"));
      }
    },
  },
};
</script>

<style scoped>
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}
</style>
